/*

? The document attachments of one chat message (a PDF), one card each (19 Oct 2026, the chat cards
? redesign, the app's look): a PDF glyph tile, the file's name, "2.1 MB · PDF" and a download button.
? Clicking the card opens the file in a new tab; the button saves it.

The server resolves each attachment to a signed `file_url` plus `file_name` / `size_bytes`
(lib/chat/resolveChatAttachment.ts). A document that didn't resolve (`exists: false`) is
left out rather than shown as a dead card. Photos keep going to ImageStack. The caption is the
message's own text, drawn under the card by the bubble.

*/

/* Common Imports */

import { styled } from "@mui/system";
import React from "react";

/* Component Imports */

import { CircularProgress, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";

/* Icon Imports */

import { FileDownloadOutlined, InsertDriveFileOutlined } from "@mui/icons-material";

/* Styled Components */

const TileList = styled("div")({
	display: "flex",
	flexDirection: "column",
	gap: "0.375rem",
	width: "17.5rem",
	maxWidth: "100%",
});

const Tile = styled("a")(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	gap: "0.75rem",
	padding: "0.625rem 0.5rem 0.625rem 0.625rem",
	borderRadius: "0.75rem",
	border: `1px solid ${theme.palette.divider}`,
	backgroundColor: theme.palette.background.paper,
	color: theme.palette.text.primary,
	textDecoration: "none",
	cursor: "pointer",
}));

/* The PDF glyph on a soft tint of the error red, the colour PDFs are known by. */
const GlyphTile = styled("div")(({ theme }) => ({
	width: "2.5rem",
	height: "2.5rem",
	borderRadius: "0.5rem",
	flexShrink: 0,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	background: alpha(theme.palette.error.main, theme.palette.mode === "dark" ? 0.16 : 0.08),
	color: theme.palette.error.main,
}));

const TileText = styled("div")({
	display: "flex",
	flexDirection: "column",
	minWidth: 0,
	flex: 1,
});

/* "12 KB", "3.4 MB". */
const humanFileSize = (bytes: number): string => {
	if (!bytes || bytes <= 0) return "";
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Saves [url] as [fileName]. A signed link on another origin ignores `download`, so the file is read
 * into the page first; if that is refused, the file opens in a new tab instead.
 */
const downloadFile = async (url: string, fileName: string): Promise<void> => {
	try {
		const response = await fetch(url);
		if (!response.ok) throw new Error("download");
		const blob: Blob = await response.blob();
		const objectUrl: string = URL.createObjectURL(blob);
		const link: HTMLAnchorElement = document.createElement("a");
		link.href = objectUrl;
		link.download = fileName;
		document.body.appendChild(link);
		link.click();
		link.remove();
		setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
	} catch {
		window.open(url, "_blank", "noopener,noreferrer");
	}
};

const ChatFileTiles = ({ files }: { files: Array<any> }) => {
	/* The file being saved, for its button's spinner. */
	const [saving, setSaving] = React.useState<string>("");
	const shown = files.filter((file: any) => file && file.exists !== false && file.file_url);
	if (shown.length === 0) return null;
	return (
		<TileList>
			{shown.map((file: any, index: number) => {
				const key: string = String(file.file_id || "") || String(index);
				const type: string = String(file.mime_type || "").split("/").pop() || "";
				const subtitle: string = [humanFileSize(Number(file.size_bytes || 0)), type.toUpperCase()]
					.filter((part: string) => part !== "")
					.join(" · ");
				const name: string = file.file_name || "Document";
				return (
					<Tile
						key={key}
						href={file.file_url}
						target="_blank"
						rel="noopener noreferrer"
					>
						<GlyphTile>
							<InsertDriveFileOutlined sx={{ fontSize: "1.25rem" }} />
						</GlyphTile>
						<TileText>
							<Typography
								variant="body2"
								sx={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
							>
								{name}
							</Typography>
							{subtitle !== "" && (
								<Typography
									variant="caption"
									color="text.secondary"
								>
									{subtitle}
								</Typography>
							)}
						</TileText>
						<IconButton
							size="small"
							aria-label="Download"
							disabled={saving === key}
							onClick={(event: React.MouseEvent) => {
								// The card itself opens the file; the button only saves it.
								event.preventDefault();
								event.stopPropagation();
								setSaving(key);
								downloadFile(file.file_url, name).finally(() => setSaving(""));
							}}
						>
							{saving === key ? (
								<CircularProgress size={16} />
							) : (
								<FileDownloadOutlined sx={{ fontSize: "1.25rem", color: "text.secondary" }} />
							)}
						</IconButton>
					</Tile>
				);
			})}
		</TileList>
	);
};

export default ChatFileTiles;
